import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Analytics } from '@vercel/analytics/react';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import { User } from 'lucide-react';
import { AccessibilityProvider } from './components/AccessibilityProvider';
import AccessibilityControls from './components/AccessibilityControls';
import { useFocusManagement } from './hooks/useFocusManagement';

// Pages
import HomePage from './pages/HomePage';
import CustomerRequestPage from './pages/CustomerRequestPage';
import RequestTrackingPage from './pages/RequestTrackingPage';
import RatingPage from './pages/RatingPage';
import DriverLoginPage from './pages/DriverLoginPage';
import DriverRegisterPage from './pages/DriverRegisterPage';
import DriverPasswordResetPage from './pages/DriverPasswordResetPage';
import DriverDashboardPage from './pages/DriverDashboardPage';
import DriverProfilePage from './pages/DriverProfilePage';
import BillingPage from './pages/BillingPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminRequestsPage from './pages/AdminRequestsPage';
import ContactPage from './pages/ContactPage';
import AccessibilityStatement from './pages/AccessibilityStatement';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';

function App() {
  return (
    <AccessibilityProvider>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </AccessibilityProvider>
  );
}

function AppContent() {
  useFocusManagement();
  
  return (
    <div className="min-h-screen bg-gray-50 font-hebrew">
      <AccessibilityControls />
      <ProfileIconButton />
      <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/request" element={<CustomerRequestPage />} />
              <Route path="/track/:requestId" element={<RequestTrackingPage />} />
              <Route path="/ratings" element={<RatingPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/accessibility" element={<AccessibilityStatement />} />
              <Route path="/driver/login" element={<DriverLoginPage />} />
              <Route path="/driver/register" element={<DriverRegisterPage />} />
              <Route path="/driver/password-reset" element={<DriverPasswordResetPage />} />
              <Route path="/admin/login" element={<AdminLoginPage />} />
              
              {/* Protected Driver Routes */}
              <Route 
                path="/driver/dashboard" 
                element={
                  <ProtectedRoute>
                    <DriverDashboardPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/driver/profile" 
                element={
                  <ProtectedRoute>
                    <DriverProfilePage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/billing" 
                element={
                  <ProtectedRoute>
                    <BillingPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Protected Admin Routes */}
              <Route 
                path="/admin/dashboard" 
                element={
                  <AdminRoute>
                    <AdminDashboardPage />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/admin/requests" 
                element={
                  <AdminRoute>
                    <AdminRequestsPage />
                  </AdminRoute>
                } 
              />
      </Routes>
      
      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
            direction: 'rtl',
            textAlign: 'right',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#22c55e',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <Analytics />
    </div>
  );
}

const ProfileIconButton = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated || location.pathname === '/driver/profile') {
    return null;
  }

  return (
    <Link
      to="/driver/profile"
      className="fixed top-4 left-4 z-50 bg-white shadow-md rounded-full p-2 hover:bg-orange-50 transition-colors"
      aria-label="מעבר לפרופיל"
    >
      <User className="h-6 w-6 text-orange-600" />
    </Link>
  );
};

export default App;
